import React, { useState } from 'react';
import { Truck, Shield, Phone, Mail, MapPin, Clock, Star, CheckCircle, Menu, X } from 'lucide-react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import { mockServices, mockTestimonials, mockStats } from '../mock';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Home = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [quoteForm, setQuoteForm] = useState({
    name: '',
    email: '',
    phone: '',
    pickupLocation: '',
    deliveryLocation: '',
    vehicleType: '',
    serviceType: '',
    message: ''
  });
  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [showDriverForm, setShowDriverForm] = useState(false);
  const [showOwnerOperatorForm, setShowOwnerOperatorForm] = useState(false);
  const [jobApplicationForm, setJobApplicationForm] = useState({
    position: '',
    name: '',
    email: '',
    phone: '',
    location: '',
    experience: '',
    cdlType: '',
    message: ''
  });

  const handleQuoteSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(`${API}/quotes`, quoteForm);
      if (response.status === 200) {
        toast.success('Quote request submitted! We\'ll contact you as soon as possible.');
        setQuoteForm({
          name: '',
          email: '',
          phone: '',
          pickupLocation: '',
          deliveryLocation: '',
          vehicleType: '',
          serviceType: '',
          message: ''
        });
      }
    } catch (error) {
      console.error('Error submitting quote:', error);
      toast.error('Failed to submit quote request. Please try again or call us directly.');
    }
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(`${API}/contacts`, contactForm);
      if (response.status === 200) {
        toast.success('Message sent successfully! We\'ll get back to you soon.');
        setContactForm({
          name: '',
          email: '',
          subject: '',
          message: ''
        });
      }
    } catch (error) {
      console.error('Error submitting contact:', error);
      toast.error('Failed to send message. Please try again or email us directly.');
    }
  };

  const handleJobApplicationSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(`${API}/job-applications`, jobApplicationForm);
      if (response.status === 200) {
        toast.success('Application submitted successfully! We\'ll contact you soon.');
        setJobApplicationForm({
          position: '',
          name: '',
          email: '',
          phone: '',
          location: '',
          experience: '',
          cdlType: '',
          message: ''
        });
        setShowDriverForm(false);
        setShowOwnerOperatorForm(false);
      }
    } catch (error) {
      console.error('Error submitting application:', error);
      toast.error('Failed to submit application. Please try again or call us directly.');
    }
  };

  const openDriverForm = () => {
    setJobApplicationForm({...jobApplicationForm, position: 'Driver'});
    setShowDriverForm(true);
  };

  const openOwnerOperatorForm = () => {
    setJobApplicationForm({...jobApplicationForm, position: 'Owner Operator'});
    setShowOwnerOperatorForm(true);
  };

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setMobileMenuOpen(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <div className="flex items-center">
              <img 
                src="https://customer-assets.emergentagent.com/job_rocketdash-fleet/artifacts/bnklcon7_Transaprent%20PNG-03.png" 
                alt="Rocketdash LLC" 
                className="h-16 w-auto"
              />
            </div>
            
            {/* Desktop Navigation */}
            <nav className="hidden md:flex space-x-8">
              <button onClick={() => scrollToSection('home')} className="text-gray-700 hover:text-[#0EA5E9] transition-colors font-medium">Home</button>
              <button onClick={() => scrollToSection('services')} className="text-gray-700 hover:text-[#0EA5E9] transition-colors font-medium">Services</button>
              <button onClick={() => scrollToSection('about')} className="text-gray-700 hover:text-[#0EA5E9] transition-colors font-medium">About</button>
              <button onClick={() => scrollToSection('quote')} className="text-gray-700 hover:text-[#0EA5E9] transition-colors font-medium">Get Quote</button>
              <button onClick={() => scrollToSection('contact')} className="text-gray-700 hover:text-[#0EA5E9] transition-colors font-medium">Contact</button>
              <button onClick={() => scrollToSection('careers')} className="text-gray-700 hover:text-[#0EA5E9] transition-colors font-medium">Careers</button>
            </nav>

            <div className="hidden md:flex items-center space-x-4">
              <a href="tel:+18057709884" className="flex items-center text-gray-700 hover:text-[#0EA5E9] transition-colors">
                <Phone className="w-4 h-4 mr-2" />
                <span className="font-medium">+1 (805) 770-9884</span>
              </a>
              <Button onClick={() => scrollToSection('quote')} className="bg-[#0EA5E9] hover:bg-[#0284C7] text-white">Get Quote</Button>
            </div>

            {/* Mobile menu button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden text-gray-700 hover:text-[#0EA5E9] transition-colors"
            >
              {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-gray-200">
            <div className="px-4 py-4 space-y-3">
              <button onClick={() => scrollToSection('home')} className="block w-full text-left px-4 py-2 text-gray-700 hover:bg-gray-50 rounded-md transition-colors">Home</button>
              <button onClick={() => scrollToSection('services')} className="block w-full text-left px-4 py-2 text-gray-700 hover:bg-gray-50 rounded-md transition-colors">Services</button>
              <button onClick={() => scrollToSection('about')} className="block w-full text-left px-4 py-2 text-gray-700 hover:bg-gray-50 rounded-md transition-colors">About</button>
              <button onClick={() => scrollToSection('quote')} className="block w-full text-left px-4 py-2 text-gray-700 hover:bg-gray-50 rounded-md transition-colors">Get Quote</button>
              <button onClick={() => scrollToSection('contact')} className="block w-full text-left px-4 py-2 text-gray-700 hover:bg-gray-50 rounded-md transition-colors">Contact</button>
              <button onClick={() => scrollToSection('careers')} className="block w-full text-left px-4 py-2 text-gray-700 hover:bg-gray-50 rounded-md transition-colors">Careers</button>
              <div className="pt-3 border-t border-gray-200">
                <a href="tel:+18057709884" className="flex items-center px-4 py-2 text-gray-700 hover:bg-gray-50 rounded-md transition-colors">
                  <Phone className="w-4 h-4 mr-2" />
                  <span>+1 (805) 770-9884</span>
                </a>
              </div>
            </div>
          </div>
        )}
      </header>

      {/* Hero Section */}
      <section id="home" className="pt-32 pb-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              <div className="inline-flex items-center px-4 py-2 bg-[#0EA5E9]/10 rounded-full">
                <Truck className="w-5 h-5 text-[#0EA5E9] mr-2" />
                <span className="text-[#0EA5E9] font-semibold text-sm">Trusted Auto Transport Solutions</span>
              </div>
              
              <h1 className="text-5xl lg:text-6xl font-bold text-gray-900 leading-tight">
                Fast & Reliable <span className="text-[#0EA5E9]">Vehicle Transport</span> Nationwide
              </h1>
              
              <p className="text-xl text-gray-600 leading-relaxed">
                Professional auto and motorcycle hauling services across the United States. 
                Get your vehicle delivered safely with Rocketdash's expert transport team.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <Button 
                  onClick={() => scrollToSection('quote')} 
                  size="lg" 
                  className="bg-[#0EA5E9] hover:bg-[#0284C7] text-white text-lg px-8 py-6 shadow-lg hover:shadow-xl transition-all"
                >
                  Get Free Quote
                </Button>
                <Button 
                  onClick={() => scrollToSection('services')} 
                  size="lg" 
                  variant="outline" 
                  className="border-2 border-gray-300 text-gray-700 hover:bg-gray-50 text-lg px-8 py-6 transition-all"
                >
                  View Services
                </Button>
              </div>

              <div className="flex items-center gap-8 pt-4">
                {mockStats.slice(0, 2).map((stat, index) => (
                  <div key={index} className="text-center">
                    <div className="text-3xl font-bold text-[#0EA5E9]">{stat.value}</div>
                    <div className="text-sm text-gray-600 mt-1">{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="relative">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <img 
                  src="https://customer-assets.emergentagent.com/job_rocketdash-fleet/artifacts/5o2v10f4_2.jpg" 
                  alt="Rocketdash Professional Vehicle Transport - Specialty Transport" 
                  className="w-full h-auto object-cover"
                  style={{ filter: 'brightness(1.05) contrast(1.08)' }}
                />
              </div>
              <div className="absolute -bottom-6 -right-6 w-48 h-48 bg-[#0EA5E9]/10 rounded-full blur-3xl"></div>
              <div className="absolute -top-6 -left-6 w-48 h-48 bg-[#0EA5E9]/10 rounded-full blur-3xl"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-20 px-4 sm:px-6 lg:px-8 bg-white relative overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img 
            src="https://customer-assets.emergentagent.com/job_rocketdash-fleet/artifacts/sd9i28pa_photo_2025-11-06_18-31-46.jpg" 
            alt="Rocketdash Fleet Background" 
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-white/90"></div>
        </div>
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
              Our <span className="text-[#0EA5E9]">Transport Services</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Professional vehicle transportation solutions tailored to your needs
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {mockServices.map((service) => (
              <Card key={service.id} className="border-2 hover:border-[#0EA5E9] transition-all hover:shadow-xl group">
                <CardHeader>
                  <div className="w-16 h-16 bg-[#0EA5E9]/10 rounded-xl flex items-center justify-center mb-4 group-hover:bg-[#0EA5E9] transition-colors">
                    {service.icon === 'truck' && <Truck className="w-8 h-8 text-[#0EA5E9] group-hover:text-white transition-colors" />}
                    {service.icon === 'shield' && <Shield className="w-8 h-8 text-[#0EA5E9] group-hover:text-white transition-colors" />}
                    {service.icon === 'bike' && <Truck className="w-8 h-8 text-[#0EA5E9] group-hover:text-white transition-colors" />}
                  </div>
                  <CardTitle className="text-2xl text-gray-900">{service.title}</CardTitle>
                  <CardDescription className="text-base text-gray-600 mt-2">{service.description}</CardDescription>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-3">
                    {service.features.map((feature, index) => (
                      <li key={index} className="flex items-start">
                        <CheckCircle className="w-5 h-5 text-[#0EA5E9] mr-3 mt-0.5 flex-shrink-0" />
                        <span className="text-gray-700">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="relative">
              <img 
                src="https://customer-assets.emergentagent.com/job_rocketdash-fleet/artifacts/r92ekpe9_photo_2025-11-06_18-30-12.jpg" 
                alt="Rocketdash Professional Vehicle Transport Service" 
                className="rounded-2xl shadow-2xl w-full h-auto object-cover"
              />
              <div className="absolute -bottom-8 -right-8 bg-[#0EA5E9] text-white p-8 rounded-xl shadow-xl">
                <div className="text-4xl font-bold">{mockStats[2].value}</div>
                <div className="text-sm mt-2">{mockStats[2].label}</div>
              </div>
            </div>

            <div className="space-y-6">
              <h2 className="text-4xl lg:text-5xl font-bold text-gray-900">
                About <span className="text-[#0EA5E9]">Rocketdash</span>
              </h2>
              
              <p className="text-lg text-gray-600 leading-relaxed">
                With over 5 years of experience in the auto transport industry, Rocketdash has become 
                a trusted name in vehicle shipping across the United States. We specialize in open and 
                enclosed car hauling, as well as motorcycle transport services.
              </p>
              
              <p className="text-lg text-gray-600 leading-relaxed">
                Our commitment to excellence, safety, and customer satisfaction has helped us transport 
                over 3,500 vehicles nationwide. Whether you're moving a classic car, luxury vehicle, 
                or motorcycle, we handle each transport with the utmost care and professionalism.
              </p>

              <div className="grid grid-cols-2 gap-6 pt-6">
                {mockStats.map((stat, index) => (
                  <div key={index} className="bg-white p-6 rounded-xl shadow-md">
                    <div className="text-3xl font-bold text-[#0EA5E9]">{stat.value}</div>
                    <div className="text-sm text-gray-600 mt-2">{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white relative overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img 
            src="https://customer-assets.emergentagent.com/job_rocketdash-fleet/artifacts/disgvsml_photo_2025-11-06_18-30-08.jpg" 
            alt="Rocketdash Transport Background" 
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-white/90"></div>
        </div>
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
              What Our <span className="text-[#0EA5E9]">Clients Say</span>
            </h2>
            <p className="text-xl text-gray-600">Trusted by thousands of satisfied customers</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {mockTestimonials.map((testimonial) => (
              <Card key={testimonial.id} className="border-2 hover:shadow-xl transition-all">
                <CardHeader>
                  <div className="flex items-center mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="w-5 h-5 text-yellow-400 fill-yellow-400" />
                    ))}
                  </div>
                  <CardDescription className="text-base text-gray-700 leading-relaxed">
                    "{testimonial.content}"
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="border-t pt-4">
                    <div className="font-semibold text-gray-900">{testimonial.name}</div>
                    <div className="text-sm text-gray-600">{testimonial.role}</div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Get Quote Section */}
      <section id="quote" className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
              Get a <span className="text-[#0EA5E9]">Free Quote</span>
            </h2>
            <p className="text-xl text-gray-600">Tell us about your transport needs and we'll get back to you as soon as possible</p>
          </div>

          <Card className="border-2 shadow-xl">
            <CardContent className="p-8">
              <form onSubmit={handleQuoteSubmit} className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="quote-name">Full Name *</Label>
                    <Input
                      id="quote-name"
                      value={quoteForm.name}
                      onChange={(e) => setQuoteForm({...quoteForm, name: e.target.value})}
                      required
                      placeholder="John Doe"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="quote-email">Email *</Label>
                    <Input
                      id="quote-email"
                      type="email"
                      value={quoteForm.email}
                      onChange={(e) => setQuoteForm({...quoteForm, email: e.target.value})}
                      required
                      placeholder="john@example.com"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="quote-phone">Phone Number *</Label>
                  <Input
                    id="quote-phone"
                    type="tel"
                    value={quoteForm.phone}
                    onChange={(e) => setQuoteForm({...quoteForm, phone: e.target.value})}
                    required
                    placeholder="(555) 123-4567"
                  />
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="pickup">Pickup Location *</Label>
                    <Input
                      id="pickup"
                      value={quoteForm.pickupLocation}
                      onChange={(e) => setQuoteForm({...quoteForm, pickupLocation: e.target.value})}
                      required
                      placeholder="City, State"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="delivery">Delivery Location *</Label>
                    <Input
                      id="delivery"
                      value={quoteForm.deliveryLocation}
                      onChange={(e) => setQuoteForm({...quoteForm, deliveryLocation: e.target.value})}
                      required
                      placeholder="City, State"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="vehicle-type">Vehicle Type *</Label>
                    <Select value={quoteForm.vehicleType} onValueChange={(value) => setQuoteForm({...quoteForm, vehicleType: value})} required>
                      <SelectTrigger id="vehicle-type">
                        <SelectValue placeholder="Select vehicle type" />
                      </SelectTrigger>
                      <SelectContent position="popper" sideOffset={5}>
                        <SelectItem value="sedan">Sedan</SelectItem>
                        <SelectItem value="suv">SUV/Truck</SelectItem>
                        <SelectItem value="luxury">Luxury Car</SelectItem>
                        <SelectItem value="classic">Classic Car</SelectItem>
                        <SelectItem value="motorcycle">Motorcycle</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="service-type">Service Type *</Label>
                    <Select value={quoteForm.serviceType} onValueChange={(value) => setQuoteForm({...quoteForm, serviceType: value})} required>
                      <SelectTrigger id="service-type">
                        <SelectValue placeholder="Select service" />
                      </SelectTrigger>
                      <SelectContent position="popper" sideOffset={5}>
                        <SelectItem value="open">Open Car Hauling</SelectItem>
                        <SelectItem value="enclosed">Enclosed Transport</SelectItem>
                        <SelectItem value="motorcycle">Motorcycle Shipping</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="quote-message">Additional Details</Label>
                  <Textarea
                    id="quote-message"
                    value={quoteForm.message}
                    onChange={(e) => setQuoteForm({...quoteForm, message: e.target.value})}
                    placeholder="Any specific requirements or questions?"
                    rows={4}
                  />
                </div>

                <Button type="submit" className="w-full bg-[#0EA5E9] hover:bg-[#0284C7] text-white py-6 text-lg font-semibold">
                  Request Free Quote
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
              Get in <span className="text-[#0EA5E9]">Touch</span>
            </h2>
            <p className="text-xl text-gray-600">Have questions? We're here to help</p>
          </div>

          <div className="grid lg:grid-cols-2 gap-12">
            <div className="space-y-8">
              <Card className="border-2 hover:shadow-lg transition-all">
                <CardContent className="p-6">
                  <div className="flex items-start">
                    <div className="w-12 h-12 bg-[#0EA5E9]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Phone className="w-6 h-6 text-[#0EA5E9]" />
                    </div>
                    <div className="ml-4">
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">Phone</h3>
                      <a href="tel:+18057709884" className="text-gray-600 hover:text-[#0EA5E9] transition-colors">+1 (805) 770-9884</a>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-2 hover:shadow-lg transition-all">
                <CardContent className="p-6">
                  <div className="flex items-start">
                    <div className="w-12 h-12 bg-[#0EA5E9]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Mail className="w-6 h-6 text-[#0EA5E9]" />
                    </div>
                    <div className="ml-4">
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">Email</h3>
                      <a href="mailto:dispatch@rocketdashtrans.com" className="text-gray-600 hover:text-[#0EA5E9] transition-colors">dispatch@rocketdashtrans.com</a>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-2 hover:shadow-lg transition-all">
                <CardContent className="p-6">
                  <div className="flex items-start">
                    <div className="w-12 h-12 bg-[#0EA5E9]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Clock className="w-6 h-6 text-[#0EA5E9]" />
                    </div>
                    <div className="ml-4">
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">Hours</h3>
                      <p className="text-gray-600">Monday - Friday: 8AM - 6PM</p>
                      <p className="text-gray-600">Saturday: 9AM - 4PM</p>
                      <p className="text-gray-600">Sunday: Closed</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            <Card className="border-2 shadow-xl">
              <CardContent className="p-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-6">Send us a Message</h3>
                <form onSubmit={handleContactSubmit} className="space-y-6">
                  <div className="space-y-2">
                    <Label htmlFor="contact-name">Name *</Label>
                    <Input
                      id="contact-name"
                      value={contactForm.name}
                      onChange={(e) => setContactForm({...contactForm, name: e.target.value})}
                      required
                      placeholder="Your name"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="contact-email">Email *</Label>
                    <Input
                      id="contact-email"
                      type="email"
                      value={contactForm.email}
                      onChange={(e) => setContactForm({...contactForm, email: e.target.value})}
                      required
                      placeholder="your@email.com"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="contact-subject">Subject *</Label>
                    <Input
                      id="contact-subject"
                      value={contactForm.subject}
                      onChange={(e) => setContactForm({...contactForm, subject: e.target.value})}
                      required
                      placeholder="How can we help?"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="contact-message">Message *</Label>
                    <Textarea
                      id="contact-message"
                      value={contactForm.message}
                      onChange={(e) => setContactForm({...contactForm, message: e.target.value})}
                      required
                      placeholder="Your message..."
                      rows={5}
                    />
                  </div>

                  <Button type="submit" className="w-full bg-[#0EA5E9] hover:bg-[#0284C7] text-white py-6 text-lg font-semibold">
                    Send Message
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Careers Section */}
      <section id="careers" className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
              Join Our <span className="text-[#0EA5E9]">Team</span>
            </h2>
            <p className="text-xl text-gray-600">Be part of a growing company that values safety, professionalism, and excellence</p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {/* Driver Position */}
            <Card className="border-2 hover:border-[#0EA5E9] transition-all hover:shadow-xl">
              <CardHeader>
                <div className="w-16 h-16 bg-[#0EA5E9]/10 rounded-xl flex items-center justify-center mb-4">
                  <Truck className="w-8 h-8 text-[#0EA5E9]" />
                </div>
                <CardTitle className="text-2xl text-gray-900">Driver Position</CardTitle>
                <CardDescription className="text-base text-gray-600 mt-2">
                  Join our team of professional drivers hauling vehicles across the country
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3 mb-6">
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-[#0EA5E9] mr-3 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">Competitive pay and benefits</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-[#0EA5E9] mr-3 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">Modern equipment and fleet</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-[#0EA5E9] mr-3 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">Home time flexibility</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-[#0EA5E9] mr-3 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">CDL Class A required</span>
                  </li>
                </ul>
                <Button onClick={openDriverForm} className="w-full bg-[#0EA5E9] hover:bg-[#0284C7] text-white">
                  Apply for Driver Position
                </Button>
              </CardContent>
            </Card>

            {/* Owner Operator Position */}
            <Card className="border-2 hover:border-[#0EA5E9] transition-all hover:shadow-xl">
              <CardHeader>
                <div className="w-16 h-16 bg-[#0EA5E9]/10 rounded-xl flex items-center justify-center mb-4">
                  <Shield className="w-8 h-8 text-[#0EA5E9]" />
                </div>
                <CardTitle className="text-2xl text-gray-900">Owner Operator</CardTitle>
                <CardDescription className="text-base text-gray-600 mt-2">
                  Partner with us and grow your business with consistent loads and support
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3 mb-6">
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-[#0EA5E9] mr-3 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">High earning potential</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-[#0EA5E9] mr-3 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">Consistent freight and routes</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-[#0EA5E9] mr-3 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">Quick pay and fuel advances</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-[#0EA5E9] mr-3 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">24/7 dispatch support</span>
                  </li>
                </ul>
                <Button onClick={openOwnerOperatorForm} className="w-full bg-[#0EA5E9] hover:bg-[#0284C7] text-white">
                  Apply as Owner Operator
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Driver Application Dialog */}
      <Dialog open={showDriverForm} onOpenChange={setShowDriverForm}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl">Driver Position Application</DialogTitle>
            <DialogDescription>Fill out the form below to apply for the driver position</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleJobApplicationSubmit} className="space-y-4 mt-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="driver-name">Full Name *</Label>
                <Input
                  id="driver-name"
                  value={jobApplicationForm.name}
                  onChange={(e) => setJobApplicationForm({...jobApplicationForm, name: e.target.value})}
                  required
                  placeholder="John Doe"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="driver-email">Email *</Label>
                <Input
                  id="driver-email"
                  type="email"
                  value={jobApplicationForm.email}
                  onChange={(e) => setJobApplicationForm({...jobApplicationForm, email: e.target.value})}
                  required
                  placeholder="john@example.com"
                />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="driver-phone">Phone *</Label>
                <Input
                  id="driver-phone"
                  type="tel"
                  value={jobApplicationForm.phone}
                  onChange={(e) => setJobApplicationForm({...jobApplicationForm, phone: e.target.value})}
                  required
                  placeholder="(555) 123-4567"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="driver-location">Current Location *</Label>
                <Input
                  id="driver-location"
                  value={jobApplicationForm.location}
                  onChange={(e) => setJobApplicationForm({...jobApplicationForm, location: e.target.value})}
                  required
                  placeholder="City, State"
                />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="driver-experience">Years of Experience *</Label>
                <Input
                  id="driver-experience"
                  value={jobApplicationForm.experience}
                  onChange={(e) => setJobApplicationForm({...jobApplicationForm, experience: e.target.value})}
                  required
                  placeholder="e.g., 5 years"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="driver-cdl">CDL Type *</Label>
                <Select value={jobApplicationForm.cdlType} onValueChange={(value) => setJobApplicationForm({...jobApplicationForm, cdlType: value})} required>
                  <SelectTrigger id="driver-cdl">
                    <SelectValue placeholder="Select CDL type" />
                  </SelectTrigger>
                  <SelectContent position="popper" sideOffset={5}>
                    <SelectItem value="class-a">Class A</SelectItem>
                    <SelectItem value="class-b">Class B</SelectItem>
                    <SelectItem value="class-c">Class C</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="driver-message">Additional Information</Label>
              <Textarea
                id="driver-message"
                value={jobApplicationForm.message}
                onChange={(e) => setJobApplicationForm({...jobApplicationForm, message: e.target.value})}
                placeholder="Tell us about your experience, qualifications, and why you'd like to join Rocketdash"
                rows={4}
              />
            </div>

            <Button type="submit" className="w-full bg-[#0EA5E9] hover:bg-[#0284C7] text-white py-6 text-lg font-semibold">
              Submit Application
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Owner Operator Application Dialog */}
      <Dialog open={showOwnerOperatorForm} onOpenChange={setShowOwnerOperatorForm}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl">Owner Operator Application</DialogTitle>
            <DialogDescription>Fill out the form below to apply as an owner operator</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleJobApplicationSubmit} className="space-y-4 mt-4">
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="oo-name">Full Name *</Label>
                <Input
                  id="oo-name"
                  value={jobApplicationForm.name}
                  onChange={(e) => setJobApplicationForm({...jobApplicationForm, name: e.target.value})}
                  required
                  placeholder="John Doe"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="oo-email">Email *</Label>
                <Input
                  id="oo-email"
                  type="email"
                  value={jobApplicationForm.email}
                  onChange={(e) => setJobApplicationForm({...jobApplicationForm, email: e.target.value})}
                  required
                  placeholder="john@example.com"
                />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="oo-phone">Phone *</Label>
                <Input
                  id="oo-phone"
                  type="tel"
                  value={jobApplicationForm.phone}
                  onChange={(e) => setJobApplicationForm({...jobApplicationForm, phone: e.target.value})}
                  required
                  placeholder="(555) 123-4567"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="oo-location">Current Location *</Label>
                <Input
                  id="oo-location"
                  value={jobApplicationForm.location}
                  onChange={(e) => setJobApplicationForm({...jobApplicationForm, location: e.target.value})}
                  required
                  placeholder="City, State"
                />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="oo-experience">Years of Experience *</Label>
                <Input
                  id="oo-experience"
                  value={jobApplicationForm.experience}
                  onChange={(e) => setJobApplicationForm({...jobApplicationForm, experience: e.target.value})}
                  required
                  placeholder="e.g., 10 years"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="oo-cdl">CDL Type *</Label>
                <Select value={jobApplicationForm.cdlType} onValueChange={(value) => setJobApplicationForm({...jobApplicationForm, cdlType: value})} required>
                  <SelectTrigger id="oo-cdl">
                    <SelectValue placeholder="Select CDL type" />
                  </SelectTrigger>
                  <SelectContent position="popper" sideOffset={5}>
                    <SelectItem value="class-a">Class A</SelectItem>
                    <SelectItem value="class-b">Class B</SelectItem>
                    <SelectItem value="class-c">Class C</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="oo-message">Additional Information</Label>
              <Textarea
                id="oo-message"
                value={jobApplicationForm.message}
                onChange={(e) => setJobApplicationForm({...jobApplicationForm, message: e.target.value})}
                placeholder="Tell us about your equipment, experience, and why you'd like to partner with Rocketdash"
                rows={4}
              />
            </div>

            <Button type="submit" className="w-full bg-[#0EA5E9] hover:bg-[#0284C7] text-white py-6 text-lg font-semibold">
              Submit Application
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-4 gap-12 mb-12">
            <div className="md:col-span-2">
              <img 
                src="https://customer-assets.emergentagent.com/job_rocketdash-fleet/artifacts/bnklcon7_Transaprent%20PNG-03.png" 
                alt="Rocketdash LLC" 
                className="h-20 w-auto mb-6"
              />
              <p className="text-gray-400 leading-relaxed mb-6">
                Professional auto and motorcycle transport services across the United States. 
                Fast, reliable, and secure vehicle shipping you can trust.
              </p>
              <div className="flex items-center gap-2">
                <div className="flex">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 text-yellow-400 fill-yellow-400" />
                  ))}
                </div>
                <span className="text-gray-400 ml-2">4.9/5 from 500+ reviews</span>
              </div>
            </div>

            <div>
              <h4 className="text-lg font-semibold mb-4">Quick Links</h4>
              <ul className="space-y-3">
                <li><button onClick={() => scrollToSection('home')} className="text-gray-400 hover:text-[#0EA5E9] transition-colors">Home</button></li>
                <li><button onClick={() => scrollToSection('services')} className="text-gray-400 hover:text-[#0EA5E9] transition-colors">Services</button></li>
                <li><button onClick={() => scrollToSection('about')} className="text-gray-400 hover:text-[#0EA5E9] transition-colors">About Us</button></li>
                <li><button onClick={() => scrollToSection('quote')} className="text-gray-400 hover:text-[#0EA5E9] transition-colors">Get Quote</button></li>
                <li><button onClick={() => scrollToSection('contact')} className="text-gray-400 hover:text-[#0EA5E9] transition-colors">Contact</button></li>
                <li><button onClick={() => scrollToSection('careers')} className="text-gray-400 hover:text-[#0EA5E9] transition-colors">Careers</button></li>
              </ul>
            </div>

            <div>
              <h4 className="text-lg font-semibold mb-4">Services</h4>
              <ul className="space-y-3">
                <li className="text-gray-400">Open Car Hauling</li>
                <li className="text-gray-400">Enclosed Transport</li>
                <li className="text-gray-400">Motorcycle Shipping</li>
                <li className="text-gray-400">Luxury Vehicle Transport</li>
                <li className="text-gray-400">Classic Car Transport</li>
              </ul>
            </div>
          </div>

          <div className="border-t border-gray-800 pt-8">
            <div className="flex flex-col md:flex-row justify-between items-center gap-4">
              <p className="text-gray-400 text-sm">
                © 2025 Rocketdash LLC. All rights reserved.
              </p>
              <div className="flex gap-6 text-sm">
                <a href="#" className="text-gray-400 hover:text-[#0EA5E9] transition-colors">Privacy Policy</a>
                <a href="#" className="text-gray-400 hover:text-[#0EA5E9] transition-colors">Terms of Service</a>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;